import { SAVED_SEARCH_NAME_MAX_LENGTH } from '@lib/shared';
import { useQuery } from '@tanstack/vue-query';
import { createSharedComposable } from '@vueuse/core';
import lodashTruncate from 'lodash-es/truncate';
import type { ComputedRef } from 'vue';

// eslint-disable-next-line regex/invalid
import { SlpSaveSearchModal } from '#components';
import { apiGetSavedSearch, getApiGetSavedSearchQueryKey, type SavedSearchEntity } from '~/apiClient';
import { ROUTE_NAMES, type RouteName, SAVED_SEARCH_EDIT_QUERY_KEY } from '~/constants/routes';

type UseSaveSearchReturn = {
    /** The SLP shows a filter worth saving: its state alone is not one. */
    canSaveSearch: ComputedRef<boolean>;
    isEditing: ComputedRef<boolean>;
    /** The SLP waits for the saved search it may edit: saving now would save it anew. */
    isLoadingEditedSearch: ComputedRef<boolean>;
    suspenseEditedSearch: () => Promise<unknown>;
    saveSearch: () => Promise<void>;
};

type EditedSavedSearch = Readonly<{ savedSearch: SavedSearchEntity; listRouteName: RouteName }>;

// Only the modal is shared. A shared scope reads the global route, which Nuxt updates after the page being entered has
// loaded: the filters it read there would be those of the page left behind.
const useSaveSearchModal = createSharedComposable(() => useOverlay().create(SlpSaveSearchModal));

export const useSaveSearch = (): UseSaveSearchReturn => {
    // COMPOSABLES
    const route = useRoute();
    const localePath = useLocalePath();
    const { isLoggedIn } = useAuthUtils();
    const { isOnboardingInProgress } = useOnboarding();
    const { openAuthModal } = useModals();
    const { getSavedSearchLabels } = useSavedSearchLabels();
    const sellingMethod = useSlpSellingMethod();
    const params = useSlpParams(sellingMethod);
    const saveSearchModal = useSaveSearchModal();

    // DATA
    const filters = computed(() => getSavedSearchFilters(params));
    const labels = computed(() =>
        getSavedSearchLabels({
            filters: filters.value,
            brandNames: filters.value.brandUrlKeys.map(
                urlKey => params.brandsOptions.value.find(option => option.urlKey === urlKey)?.label ?? urlKey
            ),
            modelName: params.modelName.value,
        })
    );
    const editedSavedSearchId = computed(() =>
        isLoggedIn.value ? (locationQueryAsOptionalString(route.query[SAVED_SEARCH_EDIT_QUERY_KEY]) ?? null) : null
    );
    // A search the user cannot manage answers 404 and is not edited: the SLP saves a new one as usual. The 404 is data, as
    // an error does not travel with the server-rendered page: the client would ask again, hydrating the button as loading.
    const {
        data: editedSavedSearchResponse,
        isLoading: isLoadingSavedSearch,
        suspense: suspenseSavedSearch,
    } = useQuery({
        queryKey: getApiGetSavedSearchQueryKey(editedSavedSearchId),
        enabled: computed(() => editedSavedSearchId.value !== null),
        queryFn: async () =>
            apiGetSavedSearch(editedSavedSearchId).catch((error: unknown) => {
                if (isFetchError(error) && error.status === 404) return null;

                throw error;
            }),
    });
    const editedSavedSearch = computed<EditedSavedSearch | null>(() => {
        if (!editedSavedSearchResponse.value) return null;

        const { savedSearch, customer } = editedSavedSearchResponse.value;

        return { savedSearch, listRouteName: customer ? ROUTE_NAMES.ACCOUNT_SALES_REP_SAVED_SEARCHES : ROUTE_NAMES.ACCOUNT_SAVED_SEARCHES };
    });

    // METHODS
    // The suspense of a disabled query never settles: the search is waited for only when there is one to edit.
    const suspenseEditedSearch = (): Promise<unknown> => (editedSavedSearchId.value === null ? Promise.resolve() : suspenseSavedSearch());

    const updateSearch = async ({ savedSearch, listRouteName }: EditedSavedSearch): Promise<void> => {
        const result = await saveSearchModal.open({
            sellingMethod: sellingMethod.value,
            filters: filters.value,
            suggestedName: savedSearch.name,
            editedSavedSearchId: savedSearch.id,
        });

        if (result) await navigateTo(localePath(listRouteName));
    };

    const saveSearch = async (): Promise<void> => {
        if (editedSavedSearch.value) return updateSearch(editedSavedSearch.value);

        // An account still onboarding has no company yet, which its searches belong to.
        if (isLoggedIn.value && isOnboardingInProgress.value && !(await openAuthModal({ action: 'SAVE_SEARCH' }))) return;

        const result = await saveSearchModal.open({
            sellingMethod: sellingMethod.value,
            filters: filters.value,
            suggestedName: lodashTruncate(labels.value.join(' · '), { length: SAVED_SEARCH_NAME_MAX_LENGTH }),
        });

        // As on the vehicle page, the contacts a guest left start the registration, which takes the search over (PT-750).
        if (result?.guestContacts) await openAuthModal({ ...result.guestContacts, action: 'MANAGE_SAVED_SEARCHES' });
    };

    return {
        canSaveSearch: computed(() => labels.value.length > 0),
        isEditing: computed(() => editedSavedSearch.value !== null),
        isLoadingEditedSearch: computed(() => isLoadingSavedSearch.value),
        suspenseEditedSearch,
        saveSearch,
    };
};
